import styled from "styled-components";
import { Rgb } from "./types";
import { BaseComponentProps } from "../types";
import "../../designTokens/variables.css";
import {
  SMALL_SQUARED_SWATCH_WIDTH_IN_PX,
  LARGE_SQUARED_SWATCH_WIDTH_IN_PX,
  SQUARED_SWATCH_WIDTH_IN_PX,
  TestIds,
} from "./constants";
import { getBackgroundColor } from "./utils/getBackgroundColor";
import { Icon } from "../Icon";

type StyledSwatchProps = {
  hex?: string;
  rgb?: Rgb;
  $disabled?: boolean;
};

const StyledSwatch = styled.div<StyledSwatchProps>`
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  background-color: ${(props) => getBackgroundColor(props)};
`;

type StyledImageSwatchProps = {
  imageUrl: string;
  $disabled?: boolean;
};

const StyledImageSwatch = styled(StyledSwatch)<StyledImageSwatchProps>`
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  background-image: url("${(props) => props.imageUrl}");
  background-position: center;
  background-size: cover;
`;

const StyledEmptySwatch = styled(StyledSwatch)`
  cursor: not-allowed;
  background-color: var(--color-neutral-000-full);
  overflow: hidden;
  position: relative;
  ::after {
    position: absolute;
    content: "";
    top: 50%;
    left: 50%;
    margin-left: -100%;
    height: 1px;
    width: 200%;
    background-color: var(--color-primary-200);
    transform-origin: center;
    transform: rotateZ(45deg);
  }
`;

type SwatchContainerProps = {
  sideLengthInPx: number;
  selected?: boolean;
};

const SwatchContainer = styled.div<SwatchContainerProps>`
  box-sizing: border-box;
  height: ${(props) => props.sideLengthInPx}px;
  width: ${(props) => props.sideLengthInPx}px;
  border: 1px solid;
  border-color: ${(props) =>
    props.selected ? "var(--color-primary-400)" : "var(--color-primary-200)"};
  border-radius: 4px;
  overflow: hidden;

  > ${StyledSwatch}, > ${StyledImageSwatch} {
    height: ${(props) => props.sideLengthInPx - 2}px;
    width: ${(props) => props.sideLengthInPx - 2}px;
  }
`;

type SwatchComponentProps = Omit<
  BaseSwatchProps,
  "sideLengthInPx" | "selected" | "testId"
>;

const SwatchComponent = ({
  hex,
  imageUrl,
  rgb,
  disabled,
  ...props
}: SwatchComponentProps) => {
  if (imageUrl) {
    return (
      <StyledImageSwatch imageUrl={imageUrl} $disabled={disabled} {...props} />
    );
  }
  if (rgb || hex) {
    return <StyledSwatch hex={hex} rgb={rgb} $disabled={disabled} {...props} />;
  }
  return (
    <StyledEmptySwatch {...props} data-testid={TestIds.EmptySwatchInner} />
  );
};

type BaseSwatchProps = SwatchProps &
  Pick<SwatchContainerProps, "sideLengthInPx">;

const BaseSwatch = ({
  hex,
  imageUrl,
  rgb,
  sideLengthInPx,
  selected,
  testId,
  ...props
}: BaseSwatchProps) => {
  return (
    <SwatchContainer sideLengthInPx={sideLengthInPx} selected={selected}>
      <SwatchComponent
        data-testid={testId}
        hex={hex}
        imageUrl={imageUrl}
        rgb={rgb}
        {...props}
      />
    </SwatchContainer>
  );
};

type SwatchProps = BaseComponentProps<
  "div",
  {
    hex?: string;
    imageUrl?: string;
    rgb?: Rgb;
    selected?: boolean;
    disabled?: boolean;
    testId?: string;
  }
>;

export const SmallSquaredSwatch = ({
  selected = false,
  disabled = false,
  testId = TestIds.SmallSquaredSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={SMALL_SQUARED_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      disabled={disabled}
      {...props}
    />
  );
};

export const SquaredSwatch = ({
  selected = false,
  testId = TestIds.SquaredSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={SQUARED_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

export const LargeSquaredSwatch = ({
  selected = false,
  testId = TestIds.LargeSquaredSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={LARGE_SQUARED_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

export const EmptySmallSquaredSwatch = ({
  selected = false,
  testId = TestIds.EmptySquaredSmallSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={SMALL_SQUARED_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

export const EmptySquaredSwatch = ({
  selected = false,
  testId = TestIds.EmptySquaredSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={SQUARED_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

export const EmptySquaredLargeSwatch = ({
  selected = false,
  testId = TestIds.EmptySquaredLargeSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={LARGE_SQUARED_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

type AddSquaredSwatchProps = BaseComponentProps<
  "button",
  {
    size?: "small" | "large";
    testId?: string;
  }
>;

const StyledAddSquaredSwatch = styled.button<AddSquaredSwatchProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-primary-100);
  border: 1px solid var(--color-primary-400);
  border-radius: 4px;

  &.small {
    width: ${SQUARED_SWATCH_WIDTH_IN_PX}px;
    height: ${SQUARED_SWATCH_WIDTH_IN_PX}px;
  }

  &.large {
    width: ${LARGE_SQUARED_SWATCH_WIDTH_IN_PX}px;
    height: ${LARGE_SQUARED_SWATCH_WIDTH_IN_PX}px;
  }

  :hover {
    cursor: pointer;
  }

  :active {
    opacity: 0.8;
  }

  :focus {
    outline: none;
  }

  svg {
    --main-color: var(--color-primary-400);
  }
`;

export const AddSquaredSwatch = ({
  size = "small",
  testId = TestIds.AddSquaredSwatch,
  ...props
}: AddSquaredSwatchProps) => {
  return (
    <StyledAddSquaredSwatch className={size} data-testid={testId} {...props}>
      <Icon
        iconName="add"
        plain
        iconSize={size === "large" ? "regular" : "small"}
      />
    </StyledAddSquaredSwatch>
  );
};
